import { LockClosedIcon } from '@heroicons/react/20/solid';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import type { FieldConfig } from '@/services/api';

const CANNOT_BE_REQUIRED = ['tags'];

interface InitiativeFieldsTabProps {
  fieldConfigs: FieldConfig[];
  editedFields: Record<string, Partial<FieldConfig>>;
  fieldConfigSaving: boolean;
  hasFieldChanges: boolean;
  onFieldUpdate: (fieldKey: string, prop: string, value: unknown) => void;
  onSave: () => void;
  onReset: () => void;
}

export function InitiativeFieldsTab({
  fieldConfigs,
  editedFields,
  fieldConfigSaving,
  hasFieldChanges,
  onFieldUpdate,
  onSave,
  onReset,
}: InitiativeFieldsTabProps) {
  const fc = useFieldConfig();

  const getFieldValue = (fieldKey: string, prop: keyof FieldConfig) => {
    if (editedFields[fieldKey] && prop in editedFields[fieldKey]) {
      return editedFields[fieldKey][prop];
    }
    const config = fieldConfigs.find((f) => f.fieldKey === fieldKey);
    return config ? config[prop] : undefined;
  };

  // Exclude: dependencies (Labs feature), hierarchy labels (managed in Hierarchy tab), title (implicit)
  const EXCLUDED_KEYS = ['dependencies', 'initiative', 'title', 'product', 'subProduct'];
  const displayedConfigs = fieldConfigs.filter((c) => !EXCLUDED_KEYS.includes(c.fieldKey));

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-text-primary">{fc.label('initiative')} Fields</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={onReset}
            disabled={!hasFieldChanges}
            className="px-3 py-1.5 text-sm font-medium text-text-secondary border border-border-default rounded-md hover:bg-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={onSave}
            disabled={!hasFieldChanges || fieldConfigSaving}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fieldConfigSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="border border-border-default rounded-md overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-background-secondary">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary w-40">Field</th>
              <th className="text-left py-2 px-4 text-xs font-semibold text-text-secondary">Label</th>
              <th className="text-center py-2 px-4 text-xs font-semibold text-text-secondary w-20">Visible</th>
              <th className="text-center py-2 px-4 text-xs font-semibold text-text-secondary w-20">Required</th>
            </tr>
          </thead>
          <tbody>
            {displayedConfigs.map((config) => {
              const currentLabel = (getFieldValue(config.fieldKey, 'label') as string) || config.label;
              const currentVisible = (getFieldValue(config.fieldKey, 'isVisible') as boolean) ?? config.isVisible;
              const currentRequired = (getFieldValue(config.fieldKey, 'isRequired') as boolean) ?? config.isRequired;
              const canBeRequired = !CANNOT_BE_REQUIRED.includes(config.fieldKey);

              return (
                <tr
                  key={config.fieldKey}
                  className={`border-b border-gray-100 last:border-0 ${config.isLocked ? 'bg-surface-subtle/50' : 'hover:bg-surface-hover'}`}
                >
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-2">
                      {config.isLocked && (
                        <LockClosedIcon className="h-3 w-3 text-text-tertiary flex-shrink-0" />
                      )}
                      <span className="text-sm text-text-primary">{fc.label(config.fieldKey)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <input
                      type="text"
                      value={currentLabel}
                      onChange={(e) => onFieldUpdate(config.fieldKey, 'label', e.target.value)}
                      className="w-full max-w-xs px-2 py-1 text-sm border border-border-default rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-background-primary"
                      maxLength={50}
                    />
                  </td>
                  <td className="py-2 px-4 text-center">
                    {config.isLocked ? (
                      <span className="text-xs text-text-secondary">Yes</span>
                    ) : (
                      <ToggleSwitch
                        enabled={currentVisible}
                        onChange={(val) => onFieldUpdate(config.fieldKey, 'isVisible', val)}
                        size="sm"
                      />
                    )}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {config.isLocked ? (
                      <span className="text-xs text-text-secondary">Yes</span>
                    ) : !canBeRequired ? (
                      <span className="text-xs text-text-tertiary">—</span>
                    ) : (
                      <ToggleSwitch
                        enabled={currentRequired}
                        onChange={(val) => onFieldUpdate(config.fieldKey, 'isRequired', val)}
                        disabled={!currentVisible}
                        size="sm"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
